import React, { useCallback } from "react";
import { Layout, Table, Typography, Alert } from 'antd';
import { useLocation, useParams } from "react-router-dom";
import type { ColumnsType } from 'antd/es/table';

import { BitmakerAPIClient } from "../../services/BitmakerApp";
import { ClientEpicTotalsData, StaffAugmentationEpicData } from "../../bitmaker/schemas/Invoices";
import { ContractsData } from "../../bitmaker/schemas/Contracts";
import { formatTime, hoursReadable } from "../../utils";
import { REDMINE_URL } from "../../constants";

const { Content } = Layout;
const { Title } = Typography;


interface Props {
}

export const InvoicesDashboard: React.FC<Props> = () => {
    const [contractsData, setContractsData] = React.useState<Array<ContractsData>>([]);
    const [epicsData, setEpicsData] = React.useState<Array<ClientEpicTotalsData>>([]);
    const [staffAugmentationEpicData, setStaffAugmentationEpicData] = React.useState<Array<StaffAugmentationEpicData>>([]);
    const [missingEpics, setMissingEpics] = React.useState<string[]>([]);
    const [maintenanceDiscrepancyMessage, setMaintenanceDiscrepancyMessage] = React.useState<string | null>(null);
    const [isDataLoaded, setIsDataLoaded] = React.useState(false);
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const clientName = queryParams.get("name");

    const contractsColumns: ColumnsType<ContractsData> = [
        {
            key: 'id',
            title: 'ID',
            dataIndex: 'id',
            align: 'center',
            width: '5%',
        },
        {
            key: 'sign_date',
            title: 'Sign Date',
            dataIndex: 'sign_date',
            align: 'center',
            width: '15%',
        },
        {
            key: 'duration',
            title: 'Duration',
            dataIndex: 'duration',
            align: 'center',
            width: '15%',
        },
        {
            key: 'active',
            title: 'Status',
            align: 'center',
            dataIndex: 'active',
            width: '10%',
            render: (_, record) => (
                record.active ? (
                    <p>Active</p>
                ) : (
                    <p>Inactive</p>
                )
            ),
        },
        {
            key: 'finish_date',
            title: 'Finish Date',
            dataIndex: 'finish_date',
            align: 'center',
            width: '15%',
        },
        {
            key: 'value',
            title: 'Value',
            dataIndex: 'value',
            align: 'center',
            width: '15%',
        },
        {
            key: 'type',
            title: 'Type',
            dataIndex: 'type',
            align: 'center',
            width: '15%',
        },
        {
            key: 'digital_contract',
            title: 'Digital Contract',
            dataIndex: 'digital_contract',
            align: 'center',
            width: '10%',
            render: (_, record) => (
                <a href={`${record.digital_contract}`} target="_blank" rel="noreferrer">
                    Contract Link
                </a>
            ),
        }
    ];

    const epicsColumns: ColumnsType<ClientEpicTotalsData> = [
        {
            title: 'Epic',
            key: 'subject',
            dataIndex: 'subject',
            align: 'center',
            width: '20%',
            render: (_, record) => (
                <a href={`${REDMINE_URL}/issues/${record.id}`} target="_blank" rel="noreferrer">
                    {record.subject}
                </a>
            )
        },
        {
            title: 'Details',
            key: 'details',
            dataIndex: 'details',
            align: 'center',
            width: '20%',
            render: (details) => (
                <Table dataSource={details} pagination={false} showHeader={false} rowKey={record => record.id}>
                    <Table.Column
                        dataIndex="subject"
                        key="subject"
                        render={(subject: string, record: { id: number }) => (
                            <a href={`${REDMINE_URL}/issues/${record.id}`} target="_blank" rel="noreferrer">
                                {subject}
                            </a>
                        )}
                    />
                </Table>
            )
        },
        {
            title: 'Hours',
            key: 'hours',
            dataIndex: 'hours',
            align: 'center',
            width: '10%',
            render: (_, record) => <span>{hoursReadable(record.hours)}</span>
        },
        {
            title: 'Charge',
            key: 'charge',
            dataIndex: 'charge',
            align: 'center',
            width: '15%'
        },
        {
            title: 'Discount',
            key: 'discount',
            dataIndex: 'discount',
            align: 'center',
            width: '15%'
        },
        {
            title: 'Total',
            key: 'total',
            dataIndex: 'total',
            align: 'center',
            width: '15%',
            render: (_, record) => <span>{record.charge - record.discount}</span>
        },
    ];

    const staffAugmentationColumns: ColumnsType<StaffAugmentationEpicData> = [
        {
            title: 'Epic',
            key: 'subject',
            dataIndex: 'subject',
            align: 'center',
            width: '20%',
            render: (_, record) => (
                <a href={`${REDMINE_URL}/issues/${record.id}`} target="_blank" rel="noreferrer">
                    {record.subject}
                </a>
            )
        },
        {
            title: 'Details',
            key: 'details',
            dataIndex: 'details',
            align: 'center',
            width: '80%',
            render: (_, record) => {
                return (
                    <Table
                        columns={epicsColumns.filter(column => column.key !== 'details')}
                        dataSource={record.details}
                        size={'small'}
                        pagination={false}
                        bordered
                        rowKey={record => record.id}
                    />
                );
            }
        }
    ];

    const determineMissingEpics = useCallback(() => {
        // Check if there are "activity" type contracts
        const hasActivityContract = contractsData.some(contract => contract.type === "activity");

        // Check if there are "staff augmentation" type contracts
        const hasStaffAugmentationContract = contractsData.some(contract => contract.type === "staff augmentation");

        // Determine missing epics
        const missingEpicsArray: string[] = [];

        // Check if the "Spider Development" and "Spider Support/Maintenance" epics exist
        if (hasActivityContract) {
            const hasSpiderDevelopmentEpic = epicsData.some(epic => epic.subject === "Spider Development");
            const hasSpiderSupportMaintenanceEpic = epicsData.some(epic => epic.subject === "Spider Support/Maintenance");

            if (!hasSpiderDevelopmentEpic) {
                missingEpicsArray.push("Spider Development");
            }
            if (!hasSpiderSupportMaintenanceEpic) {
                missingEpicsArray.push("Spider Support/Maintenance");
            }
        }

        // Check if the "Staff Augmentation" epic exist
        if (hasStaffAugmentationContract) {
            const hasStaffAugmentationEpic = staffAugmentationEpicData.some(epic => epic.subject === "Staff Augmentation");

            if (!hasStaffAugmentationEpic) {
                missingEpicsArray.push("Staff Augmentation");
            }
        }

        setMissingEpics(missingEpicsArray);
    }, [contractsData, epicsData, staffAugmentationEpicData]);

    const checkMaintenanceHoursDiscrepancy = useCallback(() => {
        // Find the "Spider Support/Maintenance" epic
        const maintenanceEpic = epicsData.find(epic => epic.subject === "Spider Support/Maintenance");

        if (maintenanceEpic) {
            let message: string | null = null;

            if (!maintenanceEpic.bitmaker_settings || !maintenanceEpic.bitmaker_settings.limits) {
                // No bitmaker settings or limits specified
                message = "Maintenance hours have not been specified in the 'Spider Support/Maintenance' epic.";
            } else {
                // Find the maintenance limit
                const maintenanceLimit = maintenanceEpic.bitmaker_settings.limits.find(limit => limit.hasOwnProperty("maintenance"));
                if (!maintenanceLimit || !maintenanceLimit.maintenance || !maintenanceLimit.maintenance.time) {
                    // No maintenance limit specified
                    message = "Maintenance hours have not been specified in the 'Spider Support/Maintenance' epic.";
                } else {
                    // Get the maintenance hours
                    const maintenanceHours = formatTime(maintenanceLimit.maintenance.time);

                    // Compare the maintenance hours with the hours recorded in the epic
                    const discrepancy = maintenanceHours !== maintenanceEpic.hours;
                    if (discrepancy) {
                        message = `There is a discrepancy in the maintenance hours. Epic specifies ${maintenanceHours} hours, but recurring activities report ${maintenanceEpic.hours} hours.`;
                    }
                }
            }

            setMaintenanceDiscrepancyMessage(message);
        }
    }, [epicsData]);

    React.useEffect(() => {
        const handleContractDataResponse = (response: any) => {
            setContractsData(response.data);
        };

        const handleInvoiceDataResponse = (response: any) => {
            setEpicsData(response.data.epics);
            if (response.data.staff_augmentation_epic && Object.keys(response.data.staff_augmentation_epic).length > 0) {
                setStaffAugmentationEpicData([response.data.staff_augmentation_epic]);
            }
        };

        const fetchContractDataAndUpdateState = () => {
            return new Promise((resolve, reject) => {
                BitmakerAPIClient.get(`/contracts/${id}`, null, (response) => {
                    try {
                        handleContractDataResponse(response);
                        resolve(response);
                    } catch (error) {
                        reject(error);
                    }
                });
            });
        };

        const fetchInvoiceDataAndUpdateState = () => {
            return new Promise((resolve, reject) => {
                BitmakerAPIClient.get(`/invoices/${id}`, null, (response) => {
                    try {
                        handleInvoiceDataResponse(response);
                        resolve(response);
                    } catch (error) {
                        reject(error);
                    }
                });
            });
        };

        Promise.all([
            fetchContractDataAndUpdateState(),
            fetchInvoiceDataAndUpdateState()
        ]).then(() => {
            setIsDataLoaded(true);
        }).catch((error) => {
            console.error("Error fetching data: ", error);
        });
    }, [id]);

    React.useEffect(() => {
        if (isDataLoaded) {
            determineMissingEpics();
            checkMaintenanceHoursDiscrepancy();
        }
    }, [isDataLoaded, determineMissingEpics, checkMaintenanceHoursDiscrepancy]);

    const handleAlertClose = (epicName: string) => {
        // Remove the epic from the list of missing epics
        setMissingEpics(prevMissingEpics => prevMissingEpics.filter(epic => epic !== epicName));
    };

    return (
        <Layout>
            <Content>
                <div className="text-center mx-2 my-6">
                    <Title level={2}>Client {clientName}</Title>
                </div>
                <div className="mx-4">
                    {/* Display missing epic alerts if necessary */}
                    {isDataLoaded && missingEpics.map(epicName => {
                        let contractType = ''
                        if (epicName === "Spider Development" || epicName === "Spider Support/Maintenance") {
                            contractType = "activity"
                        } else if (epicName === "Staff Augmentation") {
                            contractType = "staff augmentation"
                        }

                        return (
                            <Alert
                                key={epicName}
                                message={`Missing Epic: ${epicName}`}
                                description={`The client "${clientName}" has a contract of type "${contractType}", so there should be a corresponding epic named "${epicName}".`}
                                type="warning"
                                showIcon
                                closable
                                onClose={() => handleAlertClose(epicName)}
                                className="my-4"
                                banner
                            />
                        );
                    })}
                    {/* Display maintenance hours discrepancy alert if necessary */}
                    {isDataLoaded && maintenanceDiscrepancyMessage && (
                        <Alert
                            message="Maintenance Hours"
                            description={maintenanceDiscrepancyMessage}
                            type="warning"
                            showIcon
                            closable
                            onClose={() => setMaintenanceDiscrepancyMessage(null)}
                            className="my-4"
                            banner
                        />
                    )}
                </div>
                <div className="table-section">
                    <div className="table-title">
                        <Title level={5}>Epics</Title>
                    </div>
                    <div className="table-scroll">
                        <Table
                            columns={epicsColumns}
                            dataSource={epicsData}
                            size={'large'}
                            pagination={false}
                            bordered
                            rowKey={record => record.id}
                        />
                    </div>
                    {staffAugmentationEpicData.length > 0 && (
                        <Table
                            columns={staffAugmentationColumns}
                            dataSource={staffAugmentationEpicData}
                            size={'large'}
                            pagination={false}
                            bordered
                            rowKey={record => record.id}
                        />
                    )}
                </div>
                <div className="table-section">
                    <div className="table-title">
                        <Title level={5}>Contracts</Title>
                    </div>
                    <div className="table-scroll">
                        <Table
                            columns={contractsColumns}
                            dataSource={contractsData}
                            size={'large'}
                            pagination={{ pageSize: 10 }}
                            bordered
                            rowKey={record => record.id}
                        />
                    </div>
                </div>
            </Content>
        </Layout>
    );
};

export default InvoicesDashboard;
